import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AlertCircle, Send } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

/**
 * AppealDialog — shown when a clipper hits "Appeal" on a rejected clip.
 * Displays the original rejection reason (read-only) and accepts the user's appeal.
 */
export function AppealDialog({ open, onOpenChange, submission, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  async function handleSubmit() {
    if (!submission?.id) return;
    if (!reason.trim()) {
      setError("Please explain why your clip should be reinstated.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/appeals", { submission_id: submission.id, reason: reason.trim() });
      toast({ title: "Appeal submitted", description: "We'll review your appeal and notify you with the decision." });
      onSubmitted?.();
      onOpenChange?.(false);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const rejectionReason = submission?.rejection_reason || submission?.reason || submission?.last_rejection_reason || "No reason provided.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="appeal-dialog" className="max-w-md border-border/60 bg-card text-card-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <AlertCircle size={16} className="text-amber-400" />
            Appeal rejected clip
          </DialogTitle>
          <DialogDescription>
            Submit a written explanation. Mods will review and notify you of the decision.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div data-testid="appeal-original-reason" className="rounded-xl border border-rose-500/20 bg-rose-500/[0.06] p-3 text-xs">
            <div className="mb-1 font-semibold uppercase tracking-wide text-rose-300">Original rejection reason</div>
            <div className="whitespace-pre-wrap text-foreground/90">{rejectionReason}</div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Explain your appeal</label>
            <Textarea
              data-testid="appeal-reason-input"
              placeholder="Why should this clip be reinstated?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[110px] border-border/60 bg-background"
            />
          </div>

          {error && (
            <div data-testid="appeal-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
              {error}
            </div>
          )}

          <Button
            data-testid="appeal-submit-button"
            className="w-full rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] disabled:opacity-50"
            disabled={busy || !reason.trim()}
            onClick={handleSubmit}
          >
            <Send size={14} /> {busy ? "Submitting..." : "Submit appeal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
